import { javascript } from '@codemirror/lang-javascript'
import CodeMirror, { EditorState } from '@uiw/react-codemirror'
import {
  Row,
  List,
  Button,
  Modal,
  Input,
  useTheme,
  useWindowContext,
  Footer,
  Page,
  Layout,
  Message, noop,
} from '@iqueue/ui-kit'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import FabricatorPresenter from '../../api/fabricatorPresenter'
import { json } from '@codemirror/lang-json'
import HelpersPresenter from '../../api/helpersPresenter'
import ModelPresenter from '../../api/modelPresenter'
import ErrorView from '../../components/errors/ErrorView'
import useTranslate, { t } from '../../hooks/useTranslate'
import './translations'
import DataModal from '../../components/DataModal'

function ModalComponent({ isOpen, onClose, onApply, currentName }) {
  return <Modal
    isOpened={ isOpen }
    onClose={ onClose }
    title={ t('model.createManufacturer') }
    footerActions={ [
      { key: 'cancel', title: t('model.cancel'), onClick: onClose },
      { key: 'apply', title: t('model.create'), submit: true, primary: true },
    ] }
    onApply={ onApply }
  >
    <Row>
      <Input value={ currentName || '' } autoFocus size={ 12 } name="name" placeholder={ t('model.manufacturerName') }/>
    </Row>
  </Modal>
}

function ModelPageContent() {
  const theme = useTheme()
  const [code, setCode] = useState('')
  const [manufacturer, setManufacturer] = useState()
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [testResult, setTestResult] = useState('')
  const [jsonResult, setJsonResult] = useState('')
  const [modelShow, setModelShow] = useState(false)
  const [formModal, setFormModal] = useState(false)
  const [currentCommandSchema, setCurrentCommandSchema] = useState({})
  const [editedModules, setEditedModules] = useState(new Set())
  const [showWarning, setShowWarning] = useState(false)
  const [testsPassed, setTestsPassed] = useState(false)
  const [formPassed, setFormPassed] = useState(true)
  const [originalModules, setOriginalModules] = useState([])
  const [pendingFabricator, setPendingFabricator] = useState(null)
  const [currentFabricator, setCurrentFabricator] = useState({})
  const [currentFabricatorName, setCurrentFabricatorName] = useState({})
  const editorRef = useRef(null)
  const { toggleScrollbars } = useWindowContext()

  const extractName = useCallback((plain) => {
    const match = plain.match(/const\s+name\s*=\s*['"]([^'"]+)['"]/)
    return match ? match[1] : ''
  }, [])

  const handleCodeChange = useCallback((index, newContent) => {
    const extractedName = extractName(newContent)
    setCurrentFabricator((prev) => ({
      ...prev,
      models: prev.models.map((module, i) =>
        i === index ? {
          ...module,
          plain: newContent,
          name: extractedName || module.name,
          commandsSchemaDefinition: currentCommandSchema,
        } : module,
      ),
    }))

    const originalCode = originalModules.find((item) => item.id === currentFabricator.id)?.models[index].plain
    setEditedModules((prev) => {
      const updatedModules = new Set(prev)
      if (newContent === originalCode) {
        updatedModules.delete(index)
      } else {
        updatedModules.add(index)
        setTestsPassed(false)
      }
      return updatedModules
    })
  }, [currentFabricator, originalModules])

  const handleApply = useCallback(async (item) => {
    if (currentFabricatorName) {
      await FabricatorPresenter.patch(`${ currentFabricatorName.id }`, {
        name: item.name,
      })

      const updatedList = await FabricatorPresenter.read()
      setManufacturer(updatedList)
      setOriginalModules(updatedList.map(item => ({ ...item })))

      const updated = updatedList.find(fab => fab.id === currentFabricatorName.id)
      if (updated) {
        setCurrentFabricator(updated)
        setCurrentFabricatorName(updated)
      }

      setIsOpen(false)
    } else {
      await FabricatorPresenter.post('', item)
      const updatedList = await FabricatorPresenter.read()
      setManufacturer(updatedList)
      setOriginalModules(updatedList.map(item => ({ ...item })))
      setCurrentFabricator(updatedList[updatedList.length - 1])
      setIsOpen(false)
    }
  }, [currentFabricatorName])

  const handleTabSelect = useCallback((index) => {
    if (editedModules.has(activeTab)) {
      setShowWarning(true)
    } else {
      setActiveTab(index)
    }
  }, [activeTab, editedModules])

  const confirmTabSwitch = useCallback(() => {
    setCurrentFabricator((prevFabricator) => {
      const updatedModules = prevFabricator.models.map((module, i) =>
        editedModules.has(i) ? { ...originalModules.find(orig => orig.id === prevFabricator.id).models[i] } : module,
      )
      return { ...prevFabricator, modules: updatedModules }
    })
    setEditedModules(new Set())
    setShowWarning(false)
    if (pendingFabricator) setCurrentFabricator(pendingFabricator)
    setPendingFabricator(null)
  }, [editedModules, originalModules, pendingFabricator])

  const handleSelectFabricator = useCallback((fabricator) => {
    if (editedModules.size > 0) {
      setPendingFabricator(fabricator)
      setShowWarning(true)
    } else {
      setCurrentFabricator(fabricator)
    }
  }, [editedModules])

  const executeCode = useCallback((code) => {
    try {
      const result = eval(code)
      return JSON.parse(JSON.stringify(result, (key, value) => {
        if (typeof value === 'function') {
          return value.toString()
        }
        return value
      }))
    } catch (error) {
      return { error: "Ошибка выполнения кода", details: error.stack }
    }
  }, [])

  const displayJsonResult = useCallback(() => {
    const activeModule = currentFabricator.models[activeTab]
    const result = executeCode(activeModule.plain)
    setJsonResult(JSON.stringify(result, null, 2))
  }, [currentFabricator, activeTab, executeCode])

  const saveChanges = useCallback(() => {
    setCurrentFabricator((prev) => {
      const updatedModules = prev.models.map((module, index) =>
        index === activeTab ? { ...module, plain: module.plain } : module,
      )

      return { ...prev, modules: updatedModules }
    })

    displayJsonResult()
  }, [currentFabricator, activeTab, displayJsonResult])

  const handleSubmitForm = useCallback(async (item) => {
    let commandsSchemaDefinition = null
    const modelToSave = currentFabricator.models[activeTab]

    const extractModelProperties = async (plain) => {

      commandsSchemaDefinition = eval(`(() => {
        ${ plain }
        return typeof commandsSchemaDefinition !== 'undefined' ? commandsSchemaDefinition : null
      })()
      `)

      setCurrentCommandSchema(commandsSchemaDefinition)
    }

    await extractModelProperties(modelToSave.plain)
    setCurrentFabricator((prevFabricator) => ({
      ...prevFabricator,
      models: prevFabricator.models.map((module, index) =>
        index === activeTab
          ? { ...module, commandsSchemaDefinition: item }
          : module,
      ),
    }))

    setFormModal(true)
    setFormPassed(false)

    return commandsSchemaDefinition
  }, [currentFabricator])

  const handleSaveModel = useCallback(async () => {
    if (!testsPassed) return
    let commandsSchemaDefinition = null

    const modelToSave = currentFabricator.models[activeTab]
    const extractModelProperties = (plain) => {
      const nameMatch = plain.match(/const\s+name\s*=\s*['"]([^'"]+)['"]/)
      const validityDaysMatch = plain.match(/const\s+validityDays\s*=\s*(\d+)/)

      commandsSchemaDefinition = eval(`(() => {
        ${ plain }
        return typeof commandsSchemaDefinition !== 'undefined' ? commandsSchemaDefinition : null
      })()
      `)

      return {
        name: nameMatch ? nameMatch[1] : modelToSave.name,
        validityDays: validityDaysMatch ? parseInt(validityDaysMatch[1], 10) : modelToSave.validityDays,
        commandsSchemaDefinition,
      }
    }

    const res = eval(modelToSave.plain)

    const updatedModel = {
      id: modelToSave.id,
      name: extractModelProperties(modelToSave.plain).name,
      plain: modelToSave.plain,
      commandsSchemaDefinition: extractModelProperties(modelToSave.plain).commandsSchemaDefinition,
      fabricatorId: currentFabricator.id,
      driver: res?.driver.toString(),
      validityDays: extractModelProperties(modelToSave.plain).validityDays,
    }

    try {
      await ModelPresenter.upsert(updatedModel)

      Message({
        type: 'success',
        timeout: 5000,
        title: t('model.saveSuccessTitle'),
        subtitle: t('model.saveSuccessSubtitle'),
      })

      setEditedModules((prevEditedModules) => {
        const updatedEditedModules = new Set(prevEditedModules)
        updatedEditedModules.delete(activeTab)
        return updatedEditedModules
      })

      setTestsPassed(false)

    } catch (error) {
      console.error("Ошибка при сохранении модели:", error)

      Message({
        type: 'error',
        timeout: 5000,
        title: t('model.saveErrorTitle'),
        subtitle: t('model.saveErrorSubtitle', { error: error.message || error }),
      })
    }
  }, [currentFabricator, activeTab, testsPassed, handleSubmitForm])

  const createModel = useCallback(async () => {
    const newModel = await ModelPresenter.post('', {
      name: '',
      driver: '',
      plain: '',
      validityDays: 0,
      commandsSchemaDefinition: {},
      fabricatorId: currentFabricator.id,
    })
    setCurrentFabricator((prevFabricator) => ({
      ...prevFabricator,
      models: [...prevFabricator.models, newModel.data],
    }))
  }, [currentFabricator])

  const handeTestModel = useCallback(async () => {
    const modelToTest = currentFabricator.models[activeTab]
    let res

    try {
      res = eval(modelToTest.plain)
    } catch (error) {
      console.error("Ошибка выполнения кода:", error)
      setTestResult(JSON.stringify({ error: "Ошибка выполнения кода", details: error.message }, null, 2))
      setTestsPassed(false)
      return
    }

    if (!res || !Array.isArray(res.mocks)) {
      console.error("Неверный формат модели или отсутствие моков")
      setTestResult(JSON.stringify({ error: "Неверный формат модели или отсутствие моков" }, null, 2))
      setTestsPassed(false)
      return
    }

    const testResults = []
    let allTestsPassed = true

    for (const mock of res.mocks) {
      const body = {
        driver: res.driver.toString(),
        bytes: [mock],
      }

      try {
        const response = await HelpersPresenter.modelsTemplateTest(body)
        const isTestSuccessful = response.data.status === 'ok'

        testResults.push({
          mock,
          status: isTestSuccessful ? '✅' : `${ response.data.message.message }`,
          ...(isTestSuccessful ? response.data.message[mock] : {}),
        })

        if (!isTestSuccessful) {
          allTestsPassed = false
        }
      } catch (error) {
        console.error(`Ошибка теста для ${ JSON.stringify(mock) }:`, error)
        testResults.push({ mock, status: '❌' })
        allTestsPassed = false
      }
    }

    setTestResult(JSON.stringify(testResults, null, 2))
    setTestsPassed(allTestsPassed)
  }, [currentFabricator, activeTab])

  const handleTabDoubleClick = useCallback((index) => {
    setActiveTab(index)
    setModelShow(true)
  }, [])

  const handleDeleteFabricator = useCallback(async (id) => {
    await FabricatorPresenter.delete(id)
    const updatedList = await FabricatorPresenter.read()
    setManufacturer(updatedList)
    setOriginalModules(updatedList.map(item => ({ ...item })))

    if (currentFabricator?.id === id) {
      setCurrentFabricator({})
    }
  }, [currentFabricator])

  useEffect(() => {
    const handleSaveShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 's') {
        event.preventDefault()
        saveChanges()
        displayJsonResult()
      }
    }
    window.addEventListener('keydown', handleSaveShortcut)
    return () => window.removeEventListener('keydown', handleSaveShortcut)
  }, [saveChanges, displayJsonResult])

  useEffect(() => {
    HelpersPresenter.modelsTemplate().then(r => {
      setCode(r)
    })
  }, [])

  useEffect(() => {
    FabricatorPresenter.read().then((items) => {
      const itemsWithModules = items.map((item) => ({
        ...item,
      }))
      setManufacturer(itemsWithModules)
      setOriginalModules(itemsWithModules.map(item => ({ ...item })))

      if (itemsWithModules.length > 0) {
        setCurrentFabricator(itemsWithModules[0])
      }
    })
  }, [])

  useEffect(() => {
    toggleScrollbars(false)
  }, [])

  return <Layout
    style={ {
      marginBottom: 0,
      flex: 1,
    } }
    horizontal
  >
    <Layout
      style={ {
        width: '22rem',
        maxWidth: '22rem',
        minWidth: '22rem',
        marginBottom: 0,
      } }
    >
      <List
        style={ { flex: 1 } }
        selected={ currentFabricator }
        selectionMode={ "item" }
        render={ (manufacturer) => ({
          id: manufacturer.id,
          title: manufacturer.name,
          // avatar: manufacturer.name.charAt(0).toUpperCase(),
          disabled: editedModules.size !== 0,
          extra: <>
            <Button
              className={ 'edit-fabricator' }
              onClick={ () => {
                setIsOpen(true)
                setCurrentFabricatorName(manufacturer)
              } }
              primary
              icon={ 'edit' }
              disabled={ manufacturer.id !== currentFabricator?.id }
            />
            <Button
              danger
              onClick={ () => handleDeleteFabricator(manufacturer?.id) }
              icon={ 'delete' }
              disabled={ manufacturer.id !== currentFabricator?.id }
            />
          </>,
        }) }
        onSelect={ handleSelectFabricator }
        items={ manufacturer || [] }
      />

      <Button
        icon="add"
        secondary
        onClick={ () => setIsOpen(true) }
        style={ {
          margin: 'calc(var(--iq-grid-spacing) / 2)',
        } }
      />

      <ModalComponent
        isOpen={ isOpen }
        onClose={ () => {
          setCurrentFabricatorName(null)
          setIsOpen(false)
        } }
        onApply={ handleApply }
        currentName={ currentFabricatorName?.name }
      />
    </Layout>

    <Layout
      style={ {
        marginBottom: 0,
        overflow: 'hidden',
        width: '100%',
      } }
    >
      <div className={ 'editor-wrapper' }>
        <Button
          icon={ 'add' }
          className={ 'add-model' }
          onClick={ createModel }
          secondary
        />

        <Tabs selectedIndex={ activeTab } onSelect={ handleTabSelect }>
          <TabList>
            { currentFabricator?.models?.map((module, index) => (
              <Tab
                key={ module.id }
                onDoubleClick={ () => handleTabDoubleClick(index) }
                className={ editedModules.has(index) ? 'react-tabs__tab--edited' : 'react-tabs__tab' }
              >
                { module.name || t('model.unnamed') }
              </Tab>
            )) }
          </TabList>

          <div>
            { currentFabricator.models?.map((module, index) => {
              return <TabPanel key={ module.id } className={ 'code-container' }>
                <CodeMirror
                  ref={ editorRef }
                  value={ module.plain || code.template }
                  theme={ theme.renderTheme === 'dark' ? 'dark' : 'light' }
                  extensions={ [javascript(), EditorState.tabSize.of(2)] }
                  onChange={ (value) => handleCodeChange(index, value) }
                />
              </TabPanel>
            }) }
          </div>
        </Tabs>
      </div>

      <Layout
        style={ {
          flex: 1,
          marginBottom: 0,
        } }
        horizontal
        fill
      >
        <div className="child-result">
          <div className="editor-header">
            <span className="help-icon" aria-description="Здесь отображается JSON-результат">?</span>
          </div>
          <CodeMirror
            value={ jsonResult || JSON.stringify({ example: "Здесь будет ваш JSON-результат" }, null, 2) }
            theme={ theme.renderTheme === 'dark' ? 'dark' : 'light' }
            extensions={ [json(), EditorState.tabSize.of(2), EditorState.readOnly.of(true)] }
            options={ { readOnly: true } }
          />
        </div>

        <div className="child-result">
          <div className="editor-header">
            <span className="help-icon" aria-description="Здесь отображается результат тестирования">?</span>
          </div>
          <CodeMirror
            value={ testResult || JSON.stringify({ example: "Здесь будет ваш JSON-результат" }, null, 2) }
            theme={ theme.renderTheme === 'dark' ? 'dark' : 'light' }
            extensions={ [json(), EditorState.tabSize.of(2), EditorState.readOnly.of(true)] }
            options={ { readOnly: true } }
          />
        </div>
      </Layout>

      <Footer>
        <Button
          title={ t('model.executeTests') }
          onClick={ handeTestModel }
          secondary
        />

        <Button
          title={ t('model.save') }
          onClick={ handleSaveModel }
          primary
          disabled={ !testsPassed && formPassed }
        />

        <Button
          title={ t('model.delete') }
          danger
          onClick={ async () => {
            const modelIdToDelete = currentFabricator?.models[activeTab].id

            try {
              await ModelPresenter.delete(modelIdToDelete)

              setCurrentFabricator((prev) => ({
                ...prev,
                models: prev.models.filter((model, index) => index !== activeTab),
              }))

              setActiveTab((prevTab) => Math.max(prevTab - 1, 0))

              Message({
                type: 'success',
                timeout: 3000,
                title: t('model.deleteSuccess'),
              })

            } catch (error) {
              Message({
                type: 'error',
                timeout: 5000,
                title: t('model.deleteSuccess'),
                subtitle: { error: error.message || error },
              })
            }
          } }
        />

      </Footer>
    </Layout>

    <Modal
      isOpened={ showWarning }
      onClose={ () => setShowWarning(false) }
      title={ t('model.unsavedChangesTitle') }
      footerActions={ [
        { key: 'cancel', title: t('model.close'), onClick: () => setShowWarning(false) },
        { key: 'confirm', title: t('model.reset'), danger: true, onClick: confirmTabSwitch },
      ] }
    >
      <p>{ t('model.unsavedChangesMessage') }</p>
    </Modal>
  </Layout>
}

export default function ModelPage() {
  const { togglePaddings, toggleScrollbars, setTitle } = useWindowContext()
  const locale = useTranslate()
  const translatedTitle = useMemo(() => t('menu.model'), [locale])

  useEffect(() => {
    setTitle(translatedTitle)
    togglePaddings(false)
    toggleScrollbars(false)
  }, [translatedTitle])

  return <Page
    component={ ModelPageContent }
    errorView={ ErrorView }
  />
}
